import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Formik, Form } from 'formik';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Box,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    Stack,
    FormControl,
    FormLabel,
} from '@chakra-ui/react';
import { Card, CardBody } from '@/components/v4/Card';
import { Link } from 'react-router-dom';
import { axios } from '@/services/axios';
import {
    Heading,
    LoadingIndicator,
    Text,
    Button,
    Input,
} from '@/components/v4';
import {
    breadCrumbStyling,
    headingStyling,
    formLabelStyling,
    textBoxStyling,
    stackSpacing,
    stackStyling,
} from '@/components/forms/new/formStyles';
import { customTabStyles, selectedTabStyles } from '@/constants';
import { ServicesProvider } from 'pages/accounts/users/services/ServicesContext';
import Services from 'pages/accounts/users/services/Services';
import useGetCompanyDefinedAttributes from '@/pages/organisations/companies/v5/queries/useGetCompanyDefinedAttributes';
import FieldToolTip from '@/components/forms/new/FieldToolTip';
import { statusToString } from '../utils';

const EditOperatorPlatformTrunk = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(true);
    const [trunkData, setTrunkData] = useState(null);
    const [trunkName, setTrunkName] = useState('');
    const [trunkNameError, setTrunkNameError] = useState('');
    const [tabIndex, setTabIndex] = useState(0);
    const [isHoveredName, setIsHoveredName] = useState(false);

    const { currentCompany } = useSelector((state) => state.navigationLists);

    const { requiredScope } = useSelector((state) => {
        return { ...state.login };
    });
    const { darkMode } = useSelector((state) => state.settings);

    // Validation function
    const validateTrunkName = (value) => {
        if (!value || value.trim() === '') {
            return 'Trunk name is required';
        }
        if (value.length < 3) {
            return 'Trunk name must be at least 3 characters';
        }
        return undefined;
    };

    // Handle form submission
    const handleSubmit = async (_, { setSubmitting }) => {
        const validationError = validateTrunkName(trunkName);
        if (validationError) {
            setTrunkNameError(validationError);
            setSubmitting(false);
            return;
        }

        try {
            await axios.post(
                '/trunk/name',
                {
                    trunkId: trunkData.id,
                    trunkName: trunkName,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                },
            );
            setTrunkNameError('');
            toast.success('Trunk updated successfully');
            navigate('/trunks');
            dispatch({
                type: 'CHANGE_TABLE_LOADING',
            });
        } catch (err) {
            toast.error(
                err?.response?.data?.message || 'Failed to update trunk',
            );
            setSubmitting(false);
        }
    };

    const handleCancel = () => {
        navigate('/trunks');
        dispatch({
            type: 'CHANGE_TABLE_LOADING',
        });
    };

    useEffect(() => {
        if (id) {
            axios
                .get(`/Trunk/${id}`)
                .then((res) => {
                    setTrunkData(res.data);
                    setTrunkName(res.data.name || '');
                    setIsLoading(false);
                })
                .catch((err) => {
                    toast.error(
                        err?.response?.data?.message || 'Failed to load trunk',
                    );
                    setIsLoading(false);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // reset navigation on company change
    useEffect(() => {
        if (trunkData?.companyID && trunkData.companyID !== currentCompany) {
            navigate('/trunks');
        }
    }, [trunkData, currentCompany, navigate]);

    // Load company defined attributes for Services component
    const { data: servicesCompany } = useGetCompanyDefinedAttributes(
        trunkData ? { id: trunkData.companyID } : { id: null },
    );

    const breadCrumbTrail = [
        { label: 'Trunks', path: '/trunks' },
        { label: 'Edit Trunk', path: '' },
    ];

    if (isLoading) {
        return <LoadingIndicator />;
    }

    return (
        <>
            <Breadcrumb sx={breadCrumbStyling}>
                {breadCrumbTrail?.map((crumb, index) => (
                    <BreadcrumbItem key={index}>
                        <BreadcrumbLink
                            as={Link}
                            to={crumb.path}
                            onClick={
                                crumb.path === '/trunks'
                                    ? () =>
                                          dispatch({
                                              type: 'CHANGE_TABLE_LOADING',
                                          })
                                    : undefined
                            }>
                            {crumb.label}
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                ))}
            </Breadcrumb>

            <Heading sx={headingStyling}>
                {trunkData?.sipHeader || trunkData?.name}
            </Heading>

            <Tabs index={tabIndex} onChange={setTabIndex} isLazy>
                <TabList borderBottom={0} mb="10px">
                    <Tab sx={customTabStyles} _selected={selectedTabStyles}>
                        General
                    </Tab>
                    <Tab sx={customTabStyles} _selected={selectedTabStyles}>
                        Services
                    </Tab>
                </TabList>

                <TabPanels>
                    <TabPanel p={0} pt={4}>
                        <Formik
                            initialValues={{
                                trunkName: trunkData?.name || '',
                            }}
                            onSubmit={handleSubmit}
                            enableReinitialize>
                            {() => (
                                <Form id="trunkForm">
                                    <Box
                                        borderRadius={15}
                                        padding="30px"
                                        maxWidth={895}
                                        ml={0}
                                        bg={darkMode ? 'dark.tcap' : 'white'}
                                        border="1px solid"
                                        borderColor={
                                            darkMode
                                                ? 'dark.tcap-borderGray'
                                                : 'gray.200'
                                        }>
                                        {trunkData?.status !== undefined && (
                                            <FormControl
                                                sx={{
                                                    margin: '0 !important',
                                                    mb: 4,
                                                }}>
                                                <Stack
                                                    direction="row"
                                                    spacing={stackSpacing}
                                                    sx={stackStyling()}>
                                                    <FormLabel
                                                        sx={formLabelStyling(
                                                            darkMode,
                                                            true,
                                                        )}>
                                                        Status
                                                    </FormLabel>
                                                    <Box
                                                        flex={1}
                                                        maxWidth="400px">
                                                        <Text>
                                                            {statusToString(
                                                                trunkData.status,
                                                            )}
                                                        </Text>
                                                    </Box>
                                                </Stack>
                                            </FormControl>
                                        )}

                                        <FormControl
                                            isInvalid={Boolean(trunkNameError)}
                                            sx={{
                                                margin: '0 !important',
                                                mb: 4,
                                            }}
                                            onMouseEnter={() =>
                                                setIsHoveredName(true)
                                            }
                                            onMouseLeave={() =>
                                                setIsHoveredName(false)
                                            }>
                                            <Stack
                                                direction="row"
                                                spacing={stackSpacing}
                                                sx={{
                                                    ...stackStyling(),
                                                    height: 'auto',
                                                }}>
                                                <FormLabel
                                                    sx={formLabelStyling(
                                                        darkMode,
                                                        true,
                                                    )}>
                                                    Trunk Name
                                                </FormLabel>
                                                <Box flex={1} maxWidth="400px">
                                                    <Input
                                                        type="text"
                                                        id="trunkName"
                                                        name="trunkName"
                                                        value={trunkName}
                                                        onChange={(e) => {
                                                            const nextValue =
                                                                e.target.value;
                                                            setTrunkName(
                                                                nextValue,
                                                            );
                                                            if (
                                                                trunkNameError
                                                            ) {
                                                                setTrunkNameError(
                                                                    validateTrunkName(
                                                                        nextValue,
                                                                    ) || '',
                                                                );
                                                            }
                                                        }}
                                                        onBlur={() =>
                                                            setTrunkNameError(
                                                                validateTrunkName(
                                                                    trunkName,
                                                                ) || '',
                                                            )
                                                        }
                                                        sx={textBoxStyling}
                                                        placeholder="Enter trunk name"
                                                    />
                                                    {trunkNameError && (
                                                        <Text
                                                            fontSize="xs"
                                                            color="red.500"
                                                            mt={1}>
                                                            {trunkNameError}
                                                        </Text>
                                                    )}
                                                </Box>
                                                {isHoveredName && (
                                                    <FieldToolTip
                                                        label="The display name for this trunk"
                                                        ariaLabel="trunk-name-tip"
                                                        left="25px"
                                                        top="25px"
                                                    />
                                                )}
                                            </Stack>
                                        </FormControl>
                                    </Box>
                                </Form>
                            )}
                        </Formik>

                        <Box
                            sx={{ marginTop: '25px', display: 'flex', gap: 2 }}>
                            <Button
                                form="trunkForm"
                                type="submit"
                                sx={{ minWidth: '120px' }}>
                                Save
                            </Button>
                            <Button
                                variant="outline"
                                sx={{ background: 'white', minWidth: '120px' }}
                                onClick={handleCancel}>
                                Cancel
                            </Button>
                        </Box>
                    </TabPanel>

                    {requiredScope >= 60 &&
                        servicesCompany &&
                        servicesCompany.length > 0 && (
                            <TabPanel p={0} pt={4}>
                                <Card
                                    borderRadius="8px"
                                    boxShadow="0px 1px 3px rgba(0, 0, 0, 0.1)"
                                    bg={darkMode ? 'dark.tcap' : null}
                                    maxWidth={895}
                                    mb={6}>
                                    <CardBody p={6} darkMode={darkMode}>
                                        <ServicesProvider
                                            key={`${id}-services`}>
                                            <Services trunkID={id} />
                                        </ServicesProvider>
                                    </CardBody>
                                </Card>

                                <Box
                                    sx={{
                                        marginTop: '25px',
                                        display: 'flex',
                                        gap: 2,
                                    }}>
                                    <Button
                                        variant="outline"
                                        sx={{
                                            background: 'white',
                                            minWidth: '120px',
                                        }}
                                        onClick={handleCancel}>
                                        Cancel
                                    </Button>
                                </Box>
                            </TabPanel>
                        )}
                </TabPanels>
            </Tabs>
        </>
    );
};

export default EditOperatorPlatformTrunk;
